<template>
  <v-card class="dialog-card">
    <!-- close confirm modal -->
    <v-dialog
      max-width="400"
      v-model="closeSafety">
      <v-card
        v-if="closeSafety"
        class="dialog-card">
        <div class="pa-3">
          <ModalHeader
            icon="mdi-alert"
            modalTitle="Are you sure?"
            @close="closeSafety = false" />
        </div>
        <div
          class="mx-3 pa-3"
          style="background: rgba(0, 0, 0, 0.06); border-radius: 5px">
          <v-card-text>
            You will lose unsaved changes, are you sure you want to close?
          </v-card-text>
        </div>

        <div class="modal-footer">
          <v-btn
            @click="closeSafety = false"
            variant="flat">
            <v-icon class="mr-2">mdi-arrow-left-circle</v-icon>
            back
          </v-btn>
          <v-spacer />
          <v-btn
            @click="handleSafetyClose()"
            variant="flat"
            dark
            style="background-color: var(--inner-background)">
            <v-icon style="font-size: 22px">mdi-check-circle</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <!-- delete confirm modal -->
    <v-dialog
      max-width="400"
      v-model="delete_dialog">
      <v-card
        v-if="delete_dialog"
        class="dialog-card">
        <div class="pa-3">
          <ModalHeader
            icon="mdi-alert"
            modalTitle="Are you sure?"
            @close="delete_dialog = false" />
        </div>
        <div
          class="mx-3 pa-3"
          style="background: rgba(0, 0, 0, 0.06); border-radius: 5px">
          <v-card-text>
            This action is irreversible, are you sure you want to delete?
          </v-card-text>
        </div>

        <div class="modal-footer">
          <v-btn
            @click="delete_dialog = false"
            variant="flat">
            <v-icon class="mr-2">mdi-arrow-left-circle</v-icon>
            back
          </v-btn>
          <v-spacer />
          <v-btn
            @click="
              onDelete();
              delete_dialog = false;
            "
            :loading="deleteLoading"
            variant="flat"
            dark
            style="background-color: var(--inner-background)">
            <v-icon style="font-size: 22px">mdi-trash-can</v-icon>
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <!-- Modal Header -->
    <ModalHeader
      :helpFileName="helpFileName"
      :icon="icon"
      :modalTitle="currentDialogTitle"
      :modalSubtitle="tabs[activeTabIndex].title"
      @close="handleClose()" />
    <!-- END Modal Header -->
    <!-- Modal Body-->
    <div
      style="display: flex; height: 100%"
      class="px-3">
      <!-- Conditionally render tabs -->
      <v-tabs
        v-if="!hideTabs"
        class="tab-container"
        density="comfortable"
        direction="vertical"
        v-model="activeTabIndex"
        background-color="transparent">
        <v-tooltip
          v-for="tab of injectedTabs"
          :key="tab.id"
          location="right"
          :text="tab.title">
          <template v-slot:activator="{ props: tooltipProps }">
            <v-tab
              v-bind="tooltipProps"
              :value="tab.id"
              v-show="tab.display">
              <v-icon>
                {{ tab.icon }}
              </v-icon>
            </v-tab>
          </template>
        </v-tooltip>
      </v-tabs>
      <div class="mx-1"></div>

      <div
        v-if="userEditObj"
        :style="
          hideTabs
            ? ''
            : 'width: calc(100% - 50px);' /* 50px is the width of the tabs / tab container */
        "
        class="modal-body">
        <!-- END TABS -->
        <v-window
          :touch="touchless()"
          class="window-container"
          direction="vertical"
          v-model="activeTabIndex">
          <v-window-item
            class="window-item"
            v-for="tab of injectedTabs"
            :value="tab.id"
            v-show="tab.display">
            <component
              :is="tab.component"
              v-bind="tab.props"
              v-on="tab.emits"
              @request-save="handleChildSave"
              @request-close="handleClose" />
          </v-window-item>
        </v-window>
      </div>
    </div>
    <!-- END Modal Body-->
    <div class="modal-footer">
      <v-btn
        v-show="!hideBottomButtons && instanceDeletable"
        :loading="deleteLoading"
        @click="delete_dialog = true"
        class="mr-2"
        style="background-color: var(--inner-background)"
        variant="flat">
        <v-icon style="font-size: 22px">mdi-trash-can</v-icon>
      </v-btn>
      <slot name="prepend-buttons"></slot>

      <v-spacer />
      <v-btn
        v-if="!hideBottomButtons"
        @click="handleClose()"
        class="mr-2"
        style="background-color: var(--inner-background)"
        variant="flat">
        Close
      </v-btn>
      <v-btn
        v-if="!hideBottomButtons && showSaveButton"
        :disabled="!changesMade"
        :loading="saveLoading"
        @click="onSave()"
        prepend-icon="mdi-content-save"
        variant="flat"
        color="primary">
        Save
      </v-btn>
      <slot name="append-buttons"></slot>
    </div>
  </v-card>
</template>
<script setup>
import { ref, reactive, watch, onBeforeMount } from 'vue';
import { difference } from '../../../utils/comparison';
import { computed } from '@vue/reactivity';
import ModalHeader from './ModalHeader.vue';
import { createPatch, applyPatch } from 'rfc6902';
import { composeSceneDataFromScene } from '../../../utils/sceneDataComposition';
import { composeSceneDataFromTemplate } from '../../../utils/sceneDataComposition';
import { deluge } from '@/stores/deluge.js';

const touchless = () => {
  return false;
};

const props = defineProps([
  'masterObj',
  'displayName',
  'helpFileName',
  'tabs',
  'saveFunc',
  'deleteFunc',
  'instanceDeletable',
  'icon',
  'startTab',
  'hideBottomButtons',
  'customState',
  'hideTabs',
]);

const currentDialogTitle = ref(props.displayName || 'Edit Item');

const hideBottomButtons = computed(() => props.hideBottomButtons === '');

onBeforeMount(() => {
  if (props.startTab) {
    activeTabIndex.value = props.startTab;
  }
  currentDialogTitle.value = props.displayName || 'Edit Item';
});

const emit = defineEmits(['close', 'locked', 'activeTab']);

const delete_dialog = ref(false);
const closeSafety = ref(false);

// Auxillary Refs
const activeTabIndex = ref(0);
const saveLoading = ref(false);
const deleteLoading = ref(false);

// Refs

// The last saved state - used as the baseline for change detection and undo.
// Updated after each successful save to establish a new baseline.
const savedBaseline = reactive(JSON.parse(JSON.stringify(props.masterObj || {})));

// This is what the user changes, this should be passed to anything that makes changes.
const userEditObj = reactive(JSON.parse(JSON.stringify(savedBaseline)));

// Used to display the save button
const showSaveButton = ref(true);

// This is where the differences between the two live, trackedChanges to be sent to the backend.
const changesMade = ref(false);
let trackedChanges = {};
let immutableComposedSD = null;

function updateImmutableSD() {
  if (userEditObj.scene_name) {
    immutableComposedSD = composeSceneDataFromScene(
      userEditObj,
      deluge.templates,
      true
    );
  } else if (userEditObj.scene_data) {
    immutableComposedSD = composeSceneDataFromTemplate(
      userEditObj,
      deluge.templates
    );
  }
}

updateImmutableSD();

// Where we watch for changes.
watch(
  userEditObj,
  () => {
    // console.log('savedBaseline', savedBaseline);
    // console.log('userEditObj', userEditObj);
    if (!savedBaseline) {
      return;
    }
    const userEditCopy = JSON.parse(JSON.stringify(userEditObj));
    const baselineCopy = JSON.parse(JSON.stringify(savedBaseline));
    let patchSvarsChange = false;

    if (userEditCopy['editableComposedSD']) {
      const userEditCopySD = userEditCopy['editableComposedSD'];
      delete userEditCopy['editableComposedSD'];
      const patchSvars = createPatch(immutableComposedSD, userEditCopySD);
      for (const op of patchSvars) {
        if (!op.path.endsWith('originState')) {
          patchSvarsChange = true;
          break;
        }
      }
    }
    const patch = createPatch(baselineCopy, userEditCopy);
    if (patch.length === 0 && !patchSvarsChange) {
      changesMade.value = false;
      trackedChanges = {};
      emit('locked', false);
    } else {
      changesMade.value = true;
      emit('locked', true);
      // Find out what the changes are and save them.
      trackedChanges = difference(savedBaseline, userEditObj);
    }

    // console.log('changes', trackedChanges);
    if (changesMade.value) {
      showSaveButton.value = true; // Show Save button if changes are made
    }
  },
  { deep: true }
);

const injectedTabs = computed(() => {
  return injectTabs(props.tabs, userEditObj);
});

async function onSave() {
  saveLoading.value = true;

  try {
    const result = await props.saveFunc(savedBaseline.uid, {
      uid: savedBaseline.uid,
      ...trackedChanges,
    });

    // result only has a status when it's bad
    if (result?.status == undefined) {
      // Update dialog title after successful save
      let newName = props.displayName;
      if (
        userEditObj.display_name &&
        typeof userEditObj.display_name === 'string' &&
        userEditObj.display_name.trim() !== ''
      ) {
        newName = userEditObj.display_name;
      } else if (
        userEditObj.name &&
        typeof userEditObj.name === 'string' &&
        userEditObj.name.trim() !== ''
      ) {
        newName = userEditObj.name;
      }
      currentDialogTitle.value = newName || 'Edit Item';

      // Update the baseline to match the current saved state
      Object.assign(savedBaseline, JSON.parse(JSON.stringify(userEditObj)));
      updateImmutableSD();

      trackedChanges = {};
      changesMade.value = false;
      emit('locked', false);
      showSaveButton.value = false; // Hide the Save button after saving
    }
  } catch (error) {
    // console.log(error);
    emit('locked', true);
  }

  emit('locked', false);
  saveLoading.value = false;
}

async function onDelete() {
  // `Are you sure you want to delete ${props.displayName}: ${
  //   props.masterObj.display_name || props.masterObj.name
  // }?`

  deleteLoading.value = true;
  try {
    await props.deleteFunc(savedBaseline.uid);
  } catch (error) {
    console.log(error);

    deleteLoading.value = false;
    return;
  }
  deleteLoading.value = false;
  // This should close as you've just deleted the template!
  emit('locked', false);
  emit('close');
}

// This isn't currently used, but was handy for debugging.
function undoChanges() {
  // We need to keep the reference to keep tracking the changes, to do so we need to use Object.assign.
  Object.assign(userEditObj, JSON.parse(JSON.stringify(savedBaseline)));
}

function handleClose() {
  if (changesMade.value === true) {
    closeSafety.value = true;
  } else {
    emit('close');
  }
}

function handleSafetyClose() {
  closeSafety.value = false;
  emit('locked', false);
  emit('close');
}

// Called when a child requests a save (e.g. Enter pressed in a name field)
// If there are changes, save and close when the save completes successfully.
// If there are no changes, just close immediately.
function handleChildSave() {
  // If a save is already in progress, ignore additional Enter presses
  if (saveLoading.value) return;

  if (changesMade.value) {
    // First press: save changes. Do NOT close immediately — the user requested save.
    // A subsequent Enter (when changesMade is false) will close the modal.
    onSave().catch(() => {
      /* onSave will set error/locked states; nothing else to do here */
    });
  } else {
    // No changes to save — close immediately
    emit('close');
  }
}

// Injects a copy into each Tab, for the child components.
function injectTabs(tabs, copyObj) {
  for (const tab of tabs) {
    if ('props' in tab === false) tab.props = {};
    tab.props.userEditObj = copyObj;
    if ('emits' in tab === false) tab.emits = {};

    if (!props.customState) continue;
    tab.props.customState = props.customState;
  }

  return tabs;
}

watch(activeTabIndex, () => emit('activeTab', activeTabIndex.value), {
  immediate: true,
});
</script>

<style scoped>
.window-container {
  flex: 1;
  background: transparent;
  overflow-y: auto;
}

.window-item {
  height: 500px;
}

/* Constrain vertical tab width so tooltips position correctly */
.tab-container :deep(.v-tab.v-tab.v-btn) {
  min-width: 40px;
  width: 40px;
  padding: 0;
  justify-content: center;
}
</style>
